/**
 * @component ry-breadcrumb
 * @category  nav
 * @summary   Navigation breadcrumb trail (Fomantic UI `.ui.breadcrumb` equivalent). Authored as a list of `<a>` or `<span>` children; the wrapper inserts a configurable separator between items and marks the last item as `aria-current="page"`. Pure display + ARIA — no events.
 *
 * Reference: Fomantic UI's breadcrumb element. ry-breadcrumb supports any
 * separator character/glyph via the `divider` attribute (default `"/"`).
 * The element is `role="navigation"` with `aria-label="Breadcrumb"` so screen
 * readers announce it as a navigation landmark.
 *
 * @attribute divider   string?   default="/"   The separator string rendered between items. Common values: `"/"`, `"›"`, `"→"`, `"|"`. Set to an empty string to render no visible separator (assistive tech still sees items as a list).
 * @attribute size      'sm' | 'lg'?            Pure CSS hook for compact / enlarged breadcrumbs.
 *
 * @content   Direct children are the breadcrumb sections. Use `<a href="...">` for navigable steps and `<span>` for non-navigable (typically the last/current step). The wrapper marks the LAST direct child with `aria-current="page"` regardless of element type.
 *
 * @dom
 *   ry-breadcrumb[divider][size?]   role="navigation"   aria-label="Breadcrumb"
 *     > {first child}                                  data-ry-target="section"
 *     > span.ry-breadcrumb__divider                    data-ry-target="divider" aria-hidden="true"  {divider char}
 *     > {middle child}                                 data-ry-target="section"
 *     > span.ry-breadcrumb__divider                    data-ry-target="divider" aria-hidden="true"  {divider char}
 *     > {last child}                                   data-ry-target="section" aria-current="page"
 *
 * @gotcha  Dividers are decorative — they get `aria-hidden="true"` so screen readers don't read the separator between sections. The breadcrumb is announced as a navigation landmark instead.
 *
 * @gotcha  The LAST child gets `aria-current="page"` automatically, regardless of element type. To opt out (e.g. last item is still a link), remove the attribute manually after setup OR put a non-current marker as a trailing dummy element.
 *
 * @gotcha  `divider=""` (empty string) renders an empty separator span — visible spacing still applies via CSS gap. Set `divider` to a non-empty character to actually show a glyph.
 *
 * @gotcha  Setup-once: adding/removing children after mount does NOT re-insert dividers or re-mark the active section. Re-render or reconstruct if the trail changes dynamically.
 *
 * @example basic
 * ```html
 * <ry-breadcrumb>
 *   <a href="/">Home</a>
 *   <a href="/docs">Docs</a>
 *   <span>Components</span>
 * </ry-breadcrumb>
 * ```
 *
 * @example custom-divider
 * ```html
 * <ry-breadcrumb divider="›">
 *   <a href="/">Home</a>
 *   <a href="/blog">Blog</a>
 *   <span>This Post</span>
 * </ry-breadcrumb>
 * ```
 *
 * @example arrow-divider
 * ```html
 * <ry-breadcrumb divider="→">
 *   <a href="/">Start</a>
 *   <a href="/step-2">Step 2</a>
 *   <span>Step 3</span>
 * </ry-breadcrumb>
 * ```
 *
 * @example small-size
 * ```html
 * <ry-breadcrumb size="sm">
 *   <a href="/">Home</a>
 *   <span>About</span>
 * </ry-breadcrumb>
 * ```
 *
 * @tutorial mount-inserts-dividers
 * ```js
 * const el = mount(`
 *   <ry-breadcrumb>
 *     <a href="/">Home</a>
 *     <a href="/docs">Docs</a>
 *     <span>Components</span>
 *   </ry-breadcrumb>
 * `);
 * const dividers = el.querySelectorAll('[data-ry-target="divider"]');
 * // 3 sections → 2 dividers
 * await expectEqual(dividers.length, 2, 'one divider between each pair of sections');
 * await expectEqual(dividers[0].textContent, '/', 'default divider is forward slash');
 * await expectEqual(dividers[1].textContent, '/');
 * ```
 *
 * @tutorial last-child-gets-aria-current-page
 * ```js
 * // Wrong assumption: aria-current="page" is left to the consumer. The
 * // wrapper marks the LAST child automatically.
 * const el = mount(`
 *   <ry-breadcrumb>
 *     <a href="/">Home</a>
 *     <span>Docs</span>
 *   </ry-breadcrumb>
 * `);
 * const sections = el.querySelectorAll('[data-ry-target="section"]');
 * await expectEqual(sections[0].hasAttribute('aria-current'), false, 'non-last sections have no aria-current');
 * await expectAttr(sections[1], 'aria-current', 'page', 'last section is aria-current="page"');
 * ```
 *
 * @tutorial custom-divider-attribute
 * ```js
 * const el = mount(`
 *   <ry-breadcrumb divider="›">
 *     <a href="/">A</a>
 *     <a href="/b">B</a>
 *   </ry-breadcrumb>
 * `);
 * const dividers = el.querySelectorAll('[data-ry-target="divider"]');
 * await expectEqual(dividers[0].textContent, '›', 'custom divider character used');
 * ```
 *
 * @tutorial empty-divider-still-renders-slot
 * ```js
 * // Wrong assumption: divider="" renders no divider element at all.
 * // It DOES — the slot is still inserted so CSS gap-based spacing applies.
 * const el = mount(`
 *   <ry-breadcrumb divider="">
 *     <a href="/">A</a>
 *     <a href="/b">B</a>
 *   </ry-breadcrumb>
 * `);
 * const dividers = el.querySelectorAll('[data-ry-target="divider"]');
 * await expectEqual(dividers.length, 1, 'divider slot still inserted with empty content');
 * await expectEqual(dividers[0].textContent, '', 'content is empty');
 * ```
 *
 * @tutorial role-and-aria-label-on-host
 * ```js
 * // a11y contract: host announces as a navigation landmark labeled "Breadcrumb".
 * const el = mount('<ry-breadcrumb><a href="/">Home</a></ry-breadcrumb>');
 * await expectAttr(el, 'role', 'navigation');
 * await expectAttr(el, 'aria-label', 'Breadcrumb');
 * ```
 *
 * @tutorial dividers-are-aria-hidden
 * ```js
 * // Dividers are decorative — screen readers skip them.
 * const el = mount(`
 *   <ry-breadcrumb>
 *     <a href="/">A</a>
 *     <a href="/b">B</a>
 *   </ry-breadcrumb>
 * `);
 * const divider = el.querySelector('[data-ry-target="divider"]');
 * await expectAttr(divider, 'aria-hidden', 'true');
 * ```
 *
 * @tutorial single-item-renders-no-divider
 * ```js
 * // Edge case: one section needs no dividers.
 * const el = mount('<ry-breadcrumb><span>Only</span></ry-breadcrumb>');
 * await expectEqual(el.querySelectorAll('[data-ry-target="divider"]').length, 0, 'no dividers for a single section');
 * ```
 *
 * @see ry-tabs
 * @see ry-button
 */

import { RyElement } from '../core/ry-element.js';

export class RyBreadcrumb extends RyElement {
  setup(): void {
    this.#applyAria();
    this.#transformMarkup();
  }

  #applyAria(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'navigation');
    if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', 'Breadcrumb');
  }

  #transformMarkup(): void {
    // Skip if already transformed
    if (this.$('[data-ry-target="section"]')) return;

    const divider = this.getAttribute('divider') ?? '/';
    const sections = Array.from(this.children) as HTMLElement[];
    if (sections.length === 0) return;

    // Tag each existing child as a section
    sections.forEach((child, index) => {
      child.setAttribute('data-ry-target', 'section');
      if (index === sections.length - 1) {
        child.setAttribute('aria-current', 'page');
      }
    });

    // Insert dividers between adjacent sections
    for (let i = 1; i < sections.length; i++) {
      const sep = document.createElement('span');
      sep.setAttribute('data-ry-target', 'divider');
      sep.setAttribute('aria-hidden', 'true');
      sep.className = 'ry-breadcrumb__divider';
      sep.textContent = divider;
      this.insertBefore(sep, sections[i]!);
    }
  }
}

customElements.define('ry-breadcrumb', RyBreadcrumb);
